import type { ReactNode } from "react";
import { Redirect, useParams } from "@/compat/router";
import { useAppSelector } from "@/redux/hooks";
import { useAuth } from "../contexts/authContext";

type RouteParams = Record<string, string | undefined>;

/** A Salesforce app id, several (any match), or a resolver from the route params. */
export type RequiredAppId =
    | string
    | string[]
    | ((params: RouteParams) => string | string[] | undefined);

interface RequiresAppWrapperProps {
    appId: RequiredAppId;
    children: ReactNode;
}

const resolveAppIds = (appId: RequiredAppId, params: RouteParams): string[] => {
    const resolved = typeof appId === "function" ? appId(params) : appId;
    if (!resolved) return [];
    return (Array.isArray(resolved) ? resolved : [resolved]).filter(Boolean);
};

/**
 * Route guard: renders its children only when the signed-in user's token holds
 * one of the given app ids for a site of the selected account. Internal WorkNest
 * staff and consultants bypass the check (they have no client `apps` entries).
 * Unentitled users are redirected to Home before the children mount, so the
 * guarded page never fires its data requests.
 */
function RequiresAppWrapper({ appId, children }: RequiresAppWrapperProps) {
    const params = useParams();
    const { isWorknestUser, isConsultant, hasAppForSites } = useAuth();
    const { selectedAccount } = useAppSelector((state) => state.account);

    const appIds = resolveAppIds(appId, params as RouteParams);

    if (appIds.length === 0 || isWorknestUser || isConsultant) return <>{children}</>;

    // The account is set after login completes; a deep link can render first.
    if (!selectedAccount) return null;

    const siteIds = [
        ...(selectedAccount.childExternalIds ?? []),
        selectedAccount.account?.externalId,
    ];

    return hasAppForSites(appIds, siteIds) ? <>{children}</> : <Redirect to="/" />;
}

export default RequiresAppWrapper;
